import OfferInclusionsList from 'components/Common/Inclusions/OfferInclusionsList'
import DatesAvailable2027CampaignLabel, { hasDatesAvailable2027CampaignTag } from 'components/Common/Labels/DatesAvailable2027CampaignLabel'
import Modal from 'LuxKit/Modal/Modal'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import SearchTileStackAmenitiesList from '../shared/SearchTileStackAmenitiesList'
import SearchTileStackInclusionsList from '../shared/SearchTileStackInclusionsList'
import SearchTileRoomBreakdown from '../shared/SearchTileRoomBreakdown'
import SearchTileFreeCancellationInclusion, { type SearchTileCancellationInclusionKind, useSearchTileCancellationInclusion } from '../shared/SearchTileFreeCancellationInclusion'
import { getCompactInclusionListLimit } from './accommodationSearchTileView'
import useOfferUrl from 'hooks/Offers/useOfferUrl'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import { reorderTileInclusionsByActiveFilters } from 'lib/offer/inclusionUtils'
import { useActiveInclusionFilters } from 'hooks/useActiveInclusionFilters'
import LoyaltyInclusions from 'loyalty/components/LoyaltyInclusions'
import useLoyaltyBenefitInclusions from 'loyalty/hooks/useLoyaltyBenefitInclusions'
import { MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import { rewriteCharterReturnFlightsGuestInclusions } from 'lib/charterFlights/charterBundleOfferPageUtils'
import { EmptyArray } from 'lib/array/arrayUtils'
import ExperiencesPassportOfferSummary from 'components/Experiences/ExperiencesPassport/ExperiencesPassportOfferSummary'
import { useMetaSearchMobileV2Enabled } from 'components/Pages/HotelSearchPage/useMetaSearchMobileV2'
import useExperiencesPassportForOffer from 'components/Experiences/ExperiencesPassport/useExperiencesPassportForOffer'
import { resolveMappedSearchFilterInclusions, shouldShowCompactMappedInclusionsOverflow, useSearchFilterCategoryInclusions } from '../shared/useSearchFilterCategoryInclusions'

const DEFAULT_CHARTER_SEARCH_ROOMS: Array<App.Occupants> = [{ adults: 2 }]

const MAX_LUX_PLUS_INCLUSIONS_TO_DISPLAY = 5
const MAX_REGULAR_INCLUSIONS_TO_DISPLAY = 2
const MAX_TOTAL_INCLUSIONS_TO_DISPLAY = MAX_LUX_PLUS_INCLUSIONS_TO_DISPLAY + MAX_REGULAR_INCLUSIONS_TO_DISPLAY
const META_SEARCH_MOBILE_V2_MAX_INCLUSIONS = 6
const MAX_VISIBLE_INCLUSIONS_TO_SHOW_INLINE_PASSPORT_EXPERIENCES = 3

interface Props {
  bestPackage?: App.HotelPackage
  filters?: App.OfferListFilters
  offer: App.HotelOffer | App.HotelOfferSummary
  offerMetaData?: App.OfferListMetaData
  doNotOpenInclusionsModal?: boolean
  maxTotalInclusions?: number
  hideFeatureTitle?: boolean
  hideRoomBreakdown?: boolean
  maxAmenitiesToShow?: number
  hideLuxPlusLabels?: boolean
  hideLoyaltyInclusions?: boolean
  mapToSearchFilterCategories?: boolean
  cancellationInclusionKind?: SearchTileCancellationInclusionKind
}

function HotelSearchTileFeatures({
  bestPackage,
  filters,
  offer,
  offerMetaData,
  doNotOpenInclusionsModal = false,
  maxTotalInclusions: maxTotalInclusionsOverride,
  hideFeatureTitle = false,
  hideRoomBreakdown = false,
  maxAmenitiesToShow,
  hideLuxPlusLabels = false,
  hideLoyaltyInclusions = false,
  mapToSearchFilterCategories = false,
  cancellationInclusionKind,
}: Props) {
  const intl = useIntl()
  const maxTotalInclusions = maxTotalInclusionsOverride ?? MAX_TOTAL_INCLUSIONS_TO_DISPLAY
  const isMetaSearchMobileV2 = useMetaSearchMobileV2Enabled()

  const isCharterBundleOffer = offer.campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)

  const { luxPlusInclusions } = useOfferInclusions(
    bestPackage,
    {
      checkIn: filters?.checkIn,
      checkOut: filters?.checkOut,
    },
  )

  const activeInclusionCategories = useActiveInclusionFilters()

  const luxPlusSearchTileInclusions = useMemo<Array<App.PackageInclusion>>(() => {
    if (offer.luxPlusTileInclusions?.length) {
      return offer.luxPlusTileInclusions
    }
    return luxPlusInclusions
  }, [luxPlusInclusions, offer.luxPlusTileInclusions])
  const displayedLuxPlusSearchTileInclusions = useMemo<Array<App.PackageInclusion>>(() => {
    return hideLuxPlusLabels || isMetaSearchMobileV2 ? EmptyArray : luxPlusSearchTileInclusions
  }, [hideLuxPlusLabels, isMetaSearchMobileV2, luxPlusSearchTileInclusions])

  const getEffectiveInclusions = useCallback(<T extends { description: string; symbol?: string }>(inclusions: Array<T>): Array<T> => {
    if (!isCharterBundleOffer) {
      return inclusions
    }

    return rewriteCharterReturnFlightsGuestInclusions(inclusions, {
      rooms: filters?.rooms?.length ? filters.rooms : DEFAULT_CHARTER_SEARCH_ROOMS,
      intl,
    })
  }, [filters?.rooms, intl, isCharterBundleOffer])

  const [
    featuresType,
    regularSearchTileFeatures,
    regularSearchTileFeaturesTitle,
  ] = useMemo(() => {
    const NoTitle = undefined // urgency label will be used instead

    if (offer.tileInclusions?.length) {
      const effectiveTileInclusions = getEffectiveInclusions(offer.tileInclusions)
      const tileInclusionsBoosted = reorderTileInclusionsByActiveFilters(effectiveTileInclusions, activeInclusionCategories)

      return [
        'inclusions',
        tileInclusionsBoosted,
        offer.tileInclusionsHeading,
      ] as const
    }

    if (offer.lowestPricePackage?.inclusions?.length) {
      const filtered = offer.lowestPricePackage.inclusions.filter((inclusion) => inclusion.displayContext === 'Rate')
      const boosted = reorderTileInclusionsByActiveFilters(getEffectiveInclusions(filtered), activeInclusionCategories)
      return [
        'inclusions',
        boosted,
        NoTitle,
      ] as const
    }

    if (offer.lowestPricePackage?.roomType.amenities.length) {
      return [
        'amenities',
        offer.lowestPricePackage.roomType.amenities,
        NoTitle,
      ] as const
    }

    const pkgWithInclusions = offer.packages.find((p) => p.inclusions?.length)
    const pkgInclusions = pkgWithInclusions?.inclusions?.filter((inclusion) => inclusion.displayContext === 'Rate')
    return [
      'amenities',
      pkgInclusions ? getEffectiveInclusions(pkgInclusions) : pkgInclusions,
      NoTitle,
    ] as const
  }, [offer, activeInclusionCategories, getEffectiveInclusions])

  const loyaltyInclusions = useLoyaltyBenefitInclusions(offer)
  // Hide Société eSIM (e.g. 1GB mobile data) on metasearch V2 to save vertical space.
  const displayedLoyaltyInclusions = hideLoyaltyInclusions || isMetaSearchMobileV2 ? EmptyArray : loyaltyInclusions
  const loyaltyCount = displayedLoyaltyInclusions.length

  const { passport } = useExperiencesPassportForOffer({ offer, filters })
  const showExperiencesPassportInline = passport && (
    doNotOpenInclusionsModal || (
      !regularSearchTileFeatures?.length || regularSearchTileFeatures.length <= MAX_VISIBLE_INCLUSIONS_TO_SHOW_INLINE_PASSPORT_EXPERIENCES
    )
  )
  const passportCount = passport?.inclusions.length ?? 0

  const maxLuxPlusInclusionsToDisplay = useMemo<number>(() => {
    if (!regularSearchTileFeatures?.length) return maxTotalInclusions
    return Math.max(maxTotalInclusions - regularSearchTileFeatures.length, MAX_LUX_PLUS_INCLUSIONS_TO_DISPLAY)
  }, [regularSearchTileFeatures, maxTotalInclusions])

  const maxRegularInclusionsToDisplay = useMemo<number>(() => {
    if (isMetaSearchMobileV2) return META_SEARCH_MOBILE_V2_MAX_INCLUSIONS
    if (!displayedLuxPlusSearchTileInclusions.length) return maxTotalInclusions
    return Math.max(maxTotalInclusions - displayedLuxPlusSearchTileInclusions.length, MAX_REGULAR_INCLUSIONS_TO_DISPLAY)
  }, [displayedLuxPlusSearchTileInclusions, isMetaSearchMobileV2, maxTotalInclusions])

  const maxAmenitiesToDisplay = isMetaSearchMobileV2 ? META_SEARCH_MOBILE_V2_MAX_INCLUSIONS : maxAmenitiesToShow
  const cancellationInclusion = useSearchTileCancellationInclusion(cancellationInclusionKind)
  const mappedSearchFilterInclusions = useSearchFilterCategoryInclusions(
    regularSearchTileFeatures ?? EmptyArray,
    maxRegularInclusionsToDisplay,
  )
  const displayedRegularInclusions = useMemo(() => {
    const mapped = resolveMappedSearchFilterInclusions(
      mapToSearchFilterCategories,
      mappedSearchFilterInclusions.inclusions,
      regularSearchTileFeatures ?? EmptyArray,
    )
    if (!mapToSearchFilterCategories || !cancellationInclusion) return mapped
    return [cancellationInclusion, ...mapped]
  }, [cancellationInclusion, mapToSearchFilterCategories, mappedSearchFilterInclusions.inclusions, regularSearchTileFeatures])
  const inclusionOverflowLabel = shouldShowCompactMappedInclusionsOverflow(
    mapToSearchFilterCategories,
    regularSearchTileFeatures?.length ?? 0,
    mappedSearchFilterInclusions.inclusions.length,
  ) ? mappedSearchFilterInclusions.showMoreLabel : undefined

  const offerUrl = useOfferUrl(offer, {
    filters,
    offerLinkIncludesFilters: true,
    bundledOfferId: offerMetaData?.bundledOfferId,
  })

  const showModal = useContext(ModalContext)
  const openAllInclusionsModal = useCallback(() => {
    if (doNotOpenInclusionsModal) return

    showModal(<Modal
      title={offer.property.name}
      primaryActionText={intl.formatMessage({
        id: 'yz1jWBepgJQnq1ifNEk6',
        defaultMessage: 'View offer',
        description: 'CTA button in hotel inclusions modal to view the offer',
      })}
      primaryActionTo={offerUrl}
    >
      <Group direction="vertical" gap={12}>
        {!!displayedLuxPlusSearchTileInclusions.length && <OfferInclusionsList type="lux-plus" inclusions={displayedLuxPlusSearchTileInclusions} />}
        <Group direction="vertical" gap={4}>
          {regularSearchTileFeatures && <OfferInclusionsList inclusions={regularSearchTileFeatures} />}
          <LoyaltyInclusions inclusions={displayedLoyaltyInclusions} variant="offer-page" />
        </Group>
        {!mapToSearchFilterCategories && !showExperiencesPassportInline && <ExperiencesPassportOfferSummary offer={offer} filters={filters} variant="search-tile" />}
      </Group>
    </Modal>)
  }, [doNotOpenInclusionsModal, showModal, offer, offerUrl, displayedLuxPlusSearchTileInclusions, regularSearchTileFeatures, displayedLoyaltyInclusions, intl, filters, mapToSearchFilterCategories, showExperiencesPassportInline])

  return <Group direction="vertical" verticalAlign="space-between" gap={12} grow noDisplayWhenEmpty>
    <Group direction="vertical" gap={12} noDisplayWhenEmpty>
      {!!displayedLuxPlusSearchTileInclusions?.length && <LuxPlusInclusions
        luxPlusInclusions={displayedLuxPlusSearchTileInclusions}
        accommodationOffer={offer}
        hideUpsellModal={!!offer.luxPlusTileInclusions?.length}
        showMinStayDescription
        truncateAfter={maxLuxPlusInclusionsToDisplay}
        onShowMoreClick={openAllInclusionsModal}
      />}
      {featuresType === 'inclusions' && (!!cancellationInclusion || !!displayedRegularInclusions.length || !!inclusionOverflowLabel || (!mapToSearchFilterCategories && (loyaltyCount > 0 || passportCount > 0))) && <Group direction="vertical" gap={4}>
        <SearchTileStackInclusionsList
          inclusions={displayedRegularInclusions}
          inclusionsTitle={hideFeatureTitle ? undefined : regularSearchTileFeaturesTitle}
          truncateAfter={getCompactInclusionListLimit(
            maxRegularInclusionsToDisplay,
            mapToSearchFilterCategories && !!cancellationInclusion,
          )}
          additionalHiddenCount={mapToSearchFilterCategories || ((regularSearchTileFeatures?.length ?? 0) + loyaltyCount + passportCount) <= maxRegularInclusionsToDisplay ? 0 : loyaltyCount}
          overflowLabel={inclusionOverflowLabel}
          variant={mapToSearchFilterCategories ? 'small' : undefined}
          lineClamp={mapToSearchFilterCategories ? 1 : undefined}
          onShowMoreClick={openAllInclusionsModal}
          // we want to show "Select packages only" label on search tile inclusions heading for SPP offers (which are LPC)
          // see https://aussiecommerce.atlassian.net/browse/HP-1012
          showSelectPackageOnlyLabel={!mapToSearchFilterCategories && offer.type === OFFER_TYPE_ALWAYS_ON}
        />
        {!mapToSearchFilterCategories && ((regularSearchTileFeatures?.length ?? 0) + loyaltyCount) <= maxRegularInclusionsToDisplay && <LoyaltyInclusions inclusions={displayedLoyaltyInclusions} variant="search-tile" />}
      </Group>}
      {featuresType === 'amenities' && <Group direction="vertical" gap={4} noDisplayWhenEmpty>
        {cancellationInclusionKind && <SearchTileFreeCancellationInclusion kind={cancellationInclusionKind} />}
        {!!regularSearchTileFeatures?.length && <SearchTileStackAmenitiesList
          amenities={regularSearchTileFeatures}
          amenitiesTitle={hideFeatureTitle ? undefined : regularSearchTileFeaturesTitle}
          truncateAfter={maxAmenitiesToDisplay}
          variant={mapToSearchFilterCategories ? 'small' : undefined}
        />}
        <LoyaltyInclusions inclusions={displayedLoyaltyInclusions} variant="search-tile" />
      </Group>}
      {!mapToSearchFilterCategories && showExperiencesPassportInline && <ExperiencesPassportOfferSummary offer={offer} filters={filters} variant="search-tile" />}
      {!mapToSearchFilterCategories && hasDatesAvailable2027CampaignTag(offer.campaignTags) && <DatesAvailable2027CampaignLabel />}
    </Group>
    {!hideRoomBreakdown && <SearchTileRoomBreakdown
      offer={offer}
      filters={filters}
      isCroCompact={mapToSearchFilterCategories}
    />}
  </Group>
}

export default HotelSearchTileFeatures
